<template>
  <section class="donut-chart">
    <p class="h3" id="title">{{ title }} n={{ membersCount }}<span class="location"></span></p>
    <figure>
      <div class="figure-content">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 50 50"
          class="donut"
          aria-labelledby="title"
          role="img"
        >
          <desc id="desc">{{ description }}</desc>
          <circle
            class="donut-segment"
            v-for="value in computedValues"
            :key="value.name"
            :stroke="value.colour"
            stroke-width="15"
            fill="transparent"
            :style="getSegment(value)"
            :r="RADIUS"
            cx="25"
            cy="25"
          >
            <title>{{ value.name }} {{ value.size }}%</title>
          </circle>
          <circle
            class="donut-hole"
            :r="RADIUS"
            cx="25"
            cy="25"
            fill="#fff"
            role="presentation"
          ></circle>
        </svg>
      </div>
      <figcaption class="figure-key">
        <p class="sr-only">Donut chart showing composition of leader types in organisation</p>
        <ul class="figure-key-list" role="presentation">
          <li v-for="value in computedValues" :key="value.name">
            <span class="shape-circle" :style="{ backgroundColor: value.colour }"></span
            >{{ value.name }} ({{ value.size }} %)
          </li>
        </ul>
      </figcaption>
    </figure>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { LeaderTypeApiModel } from "../../../api-models";

interface CircleSlice {
  name: string;
  size: number;
  offset: number;
  colour?: string;
}

const props = defineProps<{
  title: string;
  description: string;
  values: Record<string, number>;
  leaderTypeDetails: LeaderTypeApiModel[];
  membersCount: number;
}>();

const CIRCLE_LENGTH = 100;

const RADIUS = 15.91549430918952;

function getColour(name: string): string | undefined {
  return props.leaderTypeDetails?.find((v) => v.name === name)?.htmlColor;
}

const computedValues = computed<CircleSlice[]>(() => {
  const slices: CircleSlice[] = [];
  let startingPoint = 0;
  Object.keys(props.values).forEach((key) => {
    slices.push({
      name: key,
      size: props.values[key],
      offset: -Math.round(startingPoint * 100) / 100,
      colour: getColour(key),
    });
    startingPoint += props.values[key];
  });

  return slices.sort((a, b) => b.size - a.size);
});

function getSegment(slice: CircleSlice): string {
  return `stroke-dasharray: ${`${slice.size} ${CIRCLE_LENGTH - slice.size}`};
    stroke-dashoffset: ${slice.offset}`;
}
</script>

<style lang="less">
figure {
  display: flex !important;
  justify-content: space-around;
  flex-direction: row;
  margin-left: -15px !important;
  margin-right: -15px !important;
}

@media screen and (max-width: 570px) {
  figure {
    flex-direction: column !important;
  }
}
.figure-content,
.figure-key {
  flex: 1;
  padding-left: 15px;
  padding-right: 15px;
  align-self: center;
}

.figure-content svg {
  max-height: 400px;
}

.figure-key [class*="shape-"] {
  margin-right: 6px;
}

.figure-key-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.figure-key-list li {
  margin: 0 0 8px;
  padding: 0;
}

.shape-circle {
  display: inline-block;
  vertical-align: middle;
  width: 32px;
  height: 32px;
  -webkit-border-radius: 50%;
  -moz-border-radius: 50%;
  border-radius: 50%;
}
</style>
